import { FC, useState, useEffect, useRef, ReactNode } from "react";
import { Popover, Typography, Link } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FormattedMessage } from "react-intl";
import { SysTenantConfiguration } from "../../utils/appInterfaces/AppInterfaces";
import TenantInfoPopoverSkeleton from "./TenantInfoPopoverSkeleton";
import { getTenantSysConfig } from "../../config/axios/apis/tenant/tenant";
import { BO_CORE_DEFAULT_VALUE, isCancelledError, toastError } from "../../utils/AppUtils";
import { isHttpErrorMessageAvailable } from "../../config/axios/Axios";

type vertical = "top" | "center" | "bottom";
type horizontal = "left" | "center" | "right";

interface IProps {
    position?: "top" | "bottom" | "left" | "right";
    tenantId?: number;
    children?: ReactNode;
}

const PopoverWrapper = styled("div")(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: "6px",
    width: 210,
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    "@media(max-width:280px)": {
        width: "fit-content",
    },
}));

const TenantLogo = styled("img")(({ theme }) => ({
    [theme.breakpoints.up("xs")]: {
        width: "45% !important",
    },
    [theme.breakpoints.up("sm")]: {
        width: "60% !important",
    },
    [theme.breakpoints.up("md")]: {
        width: "80% !important",
    },
    [theme.breakpoints.up("lg")]: {
        width: "100% !important",
    },
    maxWidth: "100%",
    height: "auto",
    aspectRatio: "3:1",
    marginBottom: theme.spacing(2),
}));

const Title = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: "900",
    fontSize: "18px",
}));

const Description = styled(Typography)(({ theme }) => ({
    textAlign: "center",
    marginBottom: theme.spacing(1),
    fontWeight: "400",
    fontSize: "14px",
}));

const TIMEOUT_DELAY = 300;

const defaultedString = (value: any): string => (value && value !== BO_CORE_DEFAULT_VALUE ? value : "");
const defaultedColor = (value: any): any => (value !== BO_CORE_DEFAULT_VALUE ? value : null);

const buildOrganizationInfo = (info: any): SysTenantConfiguration => ({
    boiId: info.boiId,
    tenantId: info.tenantId,
    tenantName: info.tenantName,
    subdomain: defaultedString(info?.subdomain),
    primaryColor: defaultedColor(info.primaryColor),
    secondaryColor: defaultedColor(info.secondaryColor),
    applicationName: defaultedString(info.applicationName),
    facilityName: defaultedString(info.facilityName),
    tenantLogo: info?.tenantLogo || null,
    tenantDarkLogo: info?.tenantDarkLogo || null,
    tenantTitleIcon: info?.tenantTitleIcon || null,
    tenantDarkTitleIcon: info?.tenantDarkTitleIcon || null,
    supportEmail: defaultedString(info?.supportEmail),
    supportPhone: defaultedString(info?.supportPhone),
    supportPhoneRegion: defaultedString(info?.supportPhoneRegion),
    supportPageURL: defaultedString(info?.supportPageURL),
    organization: info.organization,
    tagLine: defaultedString(info?.tagLine),
    enabledInTenant: info.enableInTenant,
    hipaaCoveredEntity: info.hipaaCoveredEntity,
});

const handleTenantInfoError = (error: any): void => {
    if (isCancelledError(error)) return;
    if (!isHttpErrorMessageAvailable(error)) {
        toastError(error?.response?.data?.message ? error?.response?.data?.message : error?.message);
    }
};

const TenantInfoPopover: FC<IProps> = ({ children, position = "top", tenantId }) => {
    const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

    const [anchorOriginVertical, setAnchorOriginVertical] = useState<vertical>("top");
    const [anchorOriginHorizontal, setAnchorOriginHorizontal] = useState<horizontal>("center");
    const [transformOriginVertical, setTransformOriginVertical] = useState<vertical>("bottom");
    const [transformOriginHorizontal, setTransformOriginHorizontal] = useState<horizontal>("center");
    const [openedPopover, setOpenedPopover] = useState(false);
    const [organizationInfo, setOrganizationInfo] = useState<SysTenantConfiguration>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const popoverAnchor = useRef(null);

    useEffect(() => {
        if (!openedPopover || !tenantId) return;
        setIsLoading(true);
        getTenantSysConfig([tenantId], {}, false)
            .then((res) => {
                const info = res?.data?.[0];
                if (!info) return;
                setOrganizationInfo(buildOrganizationInfo(info));
            })
            .catch(handleTenantInfoError)
            .finally(() => setIsLoading(false));
    }, [tenantId, openedPopover]);

    useEffect(() => {
        switch (position) {
            case "bottom":
                setAnchorOriginVertical("bottom");
                setAnchorOriginHorizontal("center");
                setTransformOriginVertical("top");
                setTransformOriginHorizontal("center");
                break;
            case "left":
                setAnchorOriginVertical("center");
                setAnchorOriginHorizontal("left");
                setTransformOriginVertical("center");
                setTransformOriginHorizontal("right");
                break;
            case "right":
                setAnchorOriginVertical("center");
                setAnchorOriginHorizontal("right");
                setTransformOriginVertical("center");
                setTransformOriginHorizontal("left");
                break;
            default:
                setAnchorOriginVertical("top");
                setAnchorOriginHorizontal("center");
                setTransformOriginVertical("bottom");
                setTransformOriginHorizontal("center");
        }
    }, []);

    const handlePopoverOpen = () => {
        if (timer.current) {
            clearTimeout(timer.current);
        }

        timer.current = setTimeout(() => setOpenedPopover(true), TIMEOUT_DELAY);
    };

    const handlePopoverClose = () => {
        if (timer.current) {
            clearTimeout(timer.current);
        }

        timer.current = setTimeout(() => setOpenedPopover(false), TIMEOUT_DELAY);
    };

    const renderTenantInformation = () => {
        return organizationInfo ? (
            <>
                {organizationInfo?.tenantLogo?.publicUrl && <TenantLogo src={organizationInfo.tenantLogo.publicUrl} alt="Tenant Logo" />}
                <Title>{organizationInfo?.organization.shortOrganizationName}</Title>
                <Description>{organizationInfo?.organization?.headquarterAddress?.fullAddress}</Description>
                {organizationInfo?.supportPageURL || organizationInfo?.supportEmail || organizationInfo?.supportPhone ? (
                    <Title>
                        <FormattedMessage id="tenant-popover.label.customer-service" defaultMessage="Customer Service" />
                    </Title>
                ) : null}

                {organizationInfo?.supportPageURL ? (
                    <Description>
                        <Link href={organizationInfo?.supportPageURL} target="_blank">
                            {organizationInfo?.supportPageURL}
                        </Link>
                    </Description>
                ) : null}

                {organizationInfo?.supportEmail ? <Description>{organizationInfo?.supportEmail}</Description> : null}
                {organizationInfo?.supportPhone ? <Description>{organizationInfo?.supportPhone}</Description> : null}
            </>
        ) : (
            <Typography>
                <FormattedMessage id="tenant-info-popover.error.no-data-available" defaultMessage="No data available" />
            </Typography>
        );
    };

    return (
        <>
            <span
                ref={popoverAnchor}
                aria-owns={openedPopover && tenantId ? "mouse-over-popover" : undefined}
                aria-haspopup="true"
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
            >
                {children}
            </span>
            <Popover
                id="mouse-over-popover"
                sx={{ pointerEvents: "none" }}
                open={openedPopover && Boolean(tenantId)}
                anchorEl={() => popoverAnchor.current}
                anchorOrigin={{
                    vertical: anchorOriginVertical,
                    horizontal: anchorOriginHorizontal,
                }}
                transformOrigin={{
                    vertical: transformOriginVertical,
                    horizontal: transformOriginHorizontal,
                }}
                slotProps={{
                    paper: {
                        onMouseEnter: handlePopoverOpen,
                        onMouseLeave: handlePopoverClose,
                        sx: { pointerEvents: "auto" },
                    },
                }}
            >
                <PopoverWrapper>{isLoading ? <TenantInfoPopoverSkeleton /> : renderTenantInformation()}</PopoverWrapper>
            </Popover>
        </>
    );
};

export default TenantInfoPopover;
